/**
 * 通用滤镜参数对话框（对比 Photoshop 补全第一档缺口 阶段 3）。
 *
 * 之前**任何**滤镜都没有参数对话框——高斯模糊/USM 锐化直接拿硬编码参数跑，
 * 这是一个既有缺口，不是新滤镜专属的，这次一次性补上，新滤镜跟着复用
 * 同一个通用外壳：每种滤镜只声明自己的 {@link FieldSpec} 数组 + 默认值，
 * 不是六份手写表单（同 `LayerStyleDialog.tsx` 的声明式思路，但字段种类
 * 只有数值/勾选两种，比图层样式简单，复用 `SelectionModifyDialog.tsx`
 * 的单字段模态壳结构而不是图层样式那套列表+详情双栏布局）。
 *
 * 布尔参数（比如"单色"）沿用项目里 Adjustment.params 的既有惯例，用
 * 0/1 的 number 表示，不引入 boolean 类型分支。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { FilterKind } from '../../app/actions/filter-actions';
import styles from './FilterDialog.module.css';

export type { FilterKind };

type FieldSpec =
  | { readonly kind: 'number'; readonly key: string; readonly label: string; readonly min: number; readonly max: number; readonly step?: number; readonly unit?: string }
  | { readonly kind: 'checkbox'; readonly key: string; readonly label: string };

const TITLE: Readonly<Record<FilterKind, string>> = {
  gaussianBlur: '高斯模糊',
  unsharpMask: 'USM 锐化',
  motionBlur: '动感模糊',
  surfaceBlur: '表面模糊',
  addNoise: '添加杂色',
  emboss: '浮雕',
};

const FIELD_SPECS: Readonly<Record<FilterKind, readonly FieldSpec[]>> = {
  gaussianBlur: [{ kind: 'number', key: 'radius', label: '半径', min: 0, max: 250, unit: 'px' }],
  unsharpMask: [
    { kind: 'number', key: 'amount', label: '数量', min: 1, max: 500, unit: '%' },
    { kind: 'number', key: 'radius', label: '半径', min: 0, max: 250, unit: 'px' },
    { kind: 'number', key: 'threshold', label: '阈值', min: 0, max: 255 },
  ],
  motionBlur: [
    { kind: 'number', key: 'angle', label: '角度', min: -180, max: 180, unit: '°' },
    { kind: 'number', key: 'distance', label: '距离', min: 0, max: 200, unit: 'px' },
  ],
  surfaceBlur: [
    { kind: 'number', key: 'radius', label: '半径', min: 0, max: 100, unit: 'px' },
    { kind: 'number', key: 'threshold', label: '阈值', min: 0, max: 255 },
  ],
  addNoise: [
    { kind: 'number', key: 'amount', label: '数量', min: 0, max: 100, unit: '%' },
    { kind: 'checkbox', key: 'monochrome', label: '单色' },
  ],
  emboss: [
    { kind: 'number', key: 'angle', label: '角度', min: -180, max: 180, unit: '°' },
    { kind: 'number', key: 'height', label: '高度', min: 1, max: 10, unit: 'px' },
    { kind: 'number', key: 'amount', label: '数量', min: 0, max: 500, unit: '%' },
  ],
};

/** 默认值——恒等/最小化影响（跟调整图层新建预设同一惯例，除浮雕：它没有"恒等"这个选项）。 */
const DEFAULT_PARAMS: Readonly<Record<FilterKind, Readonly<Record<string, number>>>> = {
  gaussianBlur: { radius: 8 },
  unsharpMask: { amount: 60, radius: 3, threshold: 4 },
  motionBlur: { angle: 0, distance: 20 },
  surfaceBlur: { radius: 5, threshold: 15 },
  addNoise: { amount: 10, monochrome: 0 },
  emboss: { angle: 135, height: 3, amount: 100 },
};

export interface FilterDialogProps {
  readonly kind: FilterKind | null;
  readonly onClose: () => void;
  readonly onConfirm: (kind: FilterKind, params: Readonly<Record<string, number>>) => void;
}

export function FilterDialog(props: FilterDialogProps): JSX.Element {
  const [params, setParams] = createSignal<Readonly<Record<string, number>>>({});
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.kind !== null, () => dialogRef ?? null, props.onClose);

  // 每次换成一种新滤镜都重置成那种滤镜的默认值——组件实例贯穿应用生命周期
  createEffect(() => {
    const kind = props.kind;
    if (kind !== null) setParams(DEFAULT_PARAMS[kind]);
  });

  const setField = (key: string, value: number): void => {
    setParams({ ...params(), [key]: value });
  };

  const renderField = (field: FieldSpec): JSX.Element => {
    if (field.kind === 'checkbox') {
      const checked = (params()[field.key] ?? 0) !== 0;
      return (
        <label class={styles.row}>
          <span class={styles.label}>{field.label}</span>
          <input
            type="checkbox"
            checked={checked}
            data-testid={`filter-${field.key}`}
            onChange={(event) => setField(field.key, event.currentTarget.checked ? 1 : 0)}
          />
        </label>
      );
    }
    const value = params()[field.key] ?? field.min;
    return (
      <label class={styles.row}>
        <span class={styles.label}>{field.label}</span>
        <input
          type="number"
          class={styles.number}
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          value={value}
          data-testid={`filter-${field.key}`}
          onInput={(event) => {
            const n = Number(event.currentTarget.value);
            if (Number.isFinite(n)) setField(field.key, Math.max(field.min, Math.min(field.max, n)));
          }}
        />
        <Show when={field.unit !== undefined}>
          <span class={styles.unit}>{field.unit}</span>
        </Show>
      </label>
    );
  };

  return (
    <Show when={props.kind}>
      {(kind) => (
        <div class={styles.backdrop} onClick={props.onClose} data-testid="filter-dialog-backdrop">
          <div
            class={styles.dialog}
            role="dialog"
            ref={(el) => (dialogRef = el)}
            aria-label={TITLE[kind()]}
            data-testid="filter-dialog"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <h2 class={styles.title}>{TITLE[kind()]}</h2>

            <For each={FIELD_SPECS[kind()]}>{(field) => renderField(field)}</For>

            <div class={styles.actions}>
              <button type="button" class={styles.secondary} onClick={props.onClose}>
                取消
              </button>
              <button
                type="button"
                class={styles.primary}
                data-testid="filter-dialog-confirm"
                onClick={() => {
                  props.onConfirm(kind(), params());
                }}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
}
